import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import {
    useAuthToken,
    useRequestUrl,
    useFullCourtData,
    useCourtsByAgency,
    useAgencyCourt,
    useCurrentUser,
    useLogEntry
} from "../context/contextHooks";
import { useForm } from 'react-hook-form';
import { feeFromName, getFeeTotal, getFeesPaymentRequest } from "./miscFeeFunctions";
import API from "../http/API";
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid2 from "@mui/material/Grid2";
import Select from '@mui/material/Select'
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Backdrop from '@mui/material/Backdrop';
import SelectedFees from "./SelectedFees";
import AddFee from './AddFee';
import ContactInfo from './ContactInfo';
import ReplayIcon from "@mui/icons-material/Replay";
import PaymentsTwoToneIcon from '@mui/icons-material/PaymentsTwoTone';
import CompletePayment from '../completePayment/CompletePayment';
import ManageWidget from '../completePayment/ManageWidget';
import PaymentDetails from '../completePayment/PaymentDetails';
import FeeList from "./FeeList";
import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';
import {useSxStyles as sx} from "../styles/sxStyles";
import useMediaQuery from "@mui/material/useMediaQuery";
import useTheme from "@mui/material/styles/useTheme";
import UserReceipts from "../lookup/UserReceipts";
import AddIcCallTwoToneIcon from "@mui/icons-material/AddIcCallTwoTone";
import EnterResult from '../callLogging/EnterResult';

const MiscFees = () => {
    const navigate = useNavigate();
    const [requestUrl,] = useRequestUrl();
    const [authToken,] = useAuthToken();
    const [courtData,] = useFullCourtData();
    const [courtsByAgency,] = useCourtsByAgency();
    const [agencyCourt, setAgencyCourt] = useAgencyCourt();
    const [user,] = useCurrentUser();
    const [logEntry, setLogEntry] = useLogEntry();
    const { register, setValue } = useForm();
    const [actionResponse, setActionResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [feeData, setFeeData] = useState(null);
    const [selectedFees, setSelectedFees] = useState([]);
    const [selectedFee, setSelectedFee] = useState(null);
    const [allFees, setAllFees] = useState([]);
    const [deliveryOptions, setDeliveryOptions] = useState(null);
    const [selectedOption, setSelectedOption] = useState(null);
    const [feeTotal, setFeeTotal] = useState(0);
    const [processingFee, setProcessingFee] = useState(0);
    const contactRef = useRef();
    const [openDialog, setOpenDialog] = useState(false);
    const [paymentRequest, setPaymentRequest] = useState(null);
    const [paymentResult, setPaymentResult] = useState(null);
    const [openReceipt, setOpenReceipt] = useState(false);
    const [openFees, setOpenFees] = useState(false);
    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.down('xl'));
    const [newCall, setNewCall] = useState(false);
    const [refreshFees, setRefreshFees] = useState(false);

    const clearMessages = () => {
        setErrorMessage(null);
        setActionResponse(null);
    }

    const getFees = () => {
        clearMessages();
        setLoading(true);
        const url = `${requestUrl}/miscFees/${courtData.court.courtId}`;
        API.getRetrieve(url, 'miscFees', `Bearer ${authToken}`, setActionResponse);
    }

    const getProcessingFee = (orgAmount) => {
        clearMessages();
        setLoading(true);
        const query = {
            courtId: courtData.court.courtId,
            paymentCategory: 'MOTO',
            paymentType: 'Card',
            courtAmount: orgAmount
        }
        const url = `${requestUrl}/calculateServiceFee`;
        API.postRetrieve(url, JSON.stringify(query), 'getProcessingFee', `Bearer ${authToken}`, setActionResponse);
    }

    const payFees = () => {
        clearMessages();
        const contactResponse = contactRef.current.getContactInfo();
        if (contactResponse.error) {
            setErrorMessage(contactResponse.errorMessage);
        } else if (!processingFee && !courtData.court.noFee) {
            getProcessingFee(feeTotal);
            setRefreshFees(true);
        } else {
            setPaymentRequest(getFeesPaymentRequest(courtData.court.courtId, contactResponse.payor, selectedFees, selectedOption, feeTotal, processingFee));
        }
    }

    const addSelected = (fee) => {
        clearMessages();
        if (fee.amount > 0) {
            selectedFees.push(fee);
            const total = getFeeTotal(selectedFees, selectedOption);
            setFeeTotal(total);
            setSelectedFee(null);
            setValue('feeSelect', '');
            getProcessingFee(total);
        } else {
            setErrorMessage('To add a fee, the total amount must be greater than zero');
        }
    }

    const cancelAdd = () => {
        setSelectedFee(null);
        setValue('feeSelect', '');
    }

    const feeClicked = (e) => {
        const feeName = e.target.value;
        if (feeName) {
            setSelectedFee(feeFromName(allFees, feeName));
        } else {
            setSelectedFee(null);
        }
    }

    const optionClicked = (e) => {
        const feeName = e.target.value;
        const option = feeFromName(deliveryOptions, feeName);
        setSelectedOption(option);
        const total = getFeeTotal(selectedFees, option);
        setFeeTotal(total);
        getProcessingFee(total);
    }

    const removeSelected = (key) => {
        const fees = selectedFees.filter(f => f.key !== key);
        setSelectedFees(fees);
        if (fees && fees.length > 0) {
            const total = getFeeTotal(fees, selectedOption);
            setFeeTotal(total);
            getProcessingFee(total);
        } else {
            setFeeTotal(0);
            setProcessingFee(0);
        }
    }

    const cancelPymt = () => {
        setSelectedFees([]);
        setSelectedFee(null);
        setSelectedOption(null);
        setFeeTotal(0);
        setProcessingFee(0);
        setPaymentRequest(null);
    }

    useEffect(() => {
        if (loading && actionResponse) {
            if (!actionResponse.error) {
                if (actionResponse.message === 'miscFees') {
                    setFeeData(actionResponse.data);
                    if (actionResponse.data.miscFees) {
                        setAllFees(actionResponse.data.miscFees);
                    }
                    if (actionResponse.data.deliveryOptions) {
                        setDeliveryOptions(actionResponse.data.deliveryOptions);
                    }
                } else if (actionResponse.requestType === 'getProcessingFee') {
                    setProcessingFee(actionResponse.citepayFee);
                }
            }
            setLoading(false);
        }
    }, [actionResponse])

    useEffect(() => {
        if (courtData && courtData.court && !feeData) {
            getFees();
        }
    }, [feeData, courtData])

    useEffect(() => {
        if (paymentRequest) {
            setOpenDialog(true);
        }
    }, [paymentRequest])

    useEffect(() => {
        if (paymentResult) {
            setOpenReceipt(true);
        }
    }, [paymentResult])

    useEffect(() => {
        if (!openReceipt && paymentResult) {
            setSelectedFees([]);
            setSelectedFee(null);
            setFeeTotal(0);
            setProcessingFee(0);
            setPaymentRequest(null);
            setPaymentResult(null);
        }
    }, [openReceipt])

    useEffect(() => {
        if (!agencyCourt && courtsByAgency && courtsByAgency.courts && courtsByAgency.courts.length === 1) {
            setAgencyCourt(courtsByAgency.courts[0]);
        }
    }, [courtsByAgency, agencyCourt]);

    useEffect(() => {
        if (user && user.logCalls && !paymentResult && !logEntry) {
            navigate(-1);
        }
    }, [logEntry, paymentResult]);

    useEffect(() => {
        if (refreshFees && processingFee) {
            setRefreshFees(false);
            payFees();
        }
    }, [refreshFees, processingFee]);

    const feeResponse = {
        courtAmount: feeTotal,
        citepayFee: processingFee,
        totalAmount: (feeTotal || 0) + (processingFee || 0),
        courtId: courtData?.court?.courtId
    };

    return (<>
        { paymentRequest && !paymentResult &&
            ( courtData?.court?.neumoPayOnline
                ? <ManageWidget
                    openDialog={openDialog}
                    setOpenDialog={setOpenDialog}
                    paymentRequest={paymentRequest}
                    setPaymentRequest={setPaymentRequest}
                    setPaymentResult={setPaymentResult}
                    court={courtData.court}
                    feeResponse={feeResponse}
                    cancelPymt={cancelPymt}
                />
                : <CompletePayment
                    openDialog={openDialog}
                    setOpenDialog={setOpenDialog}
                    paymentRequest={paymentRequest}
                    setPaymentResult={setPaymentResult}
                    court={courtData.court}
                />
            )
        }
        { paymentResult &&
            <PaymentDetails
                paymentRequest={paymentRequest}
                paymentResult={paymentResult}
                openReceipt={openReceipt}
                setOpenReceipt={setOpenReceipt}
            />
        }
        { openFees &&
            <FeeList openFees={openFees} setOpenFees={setOpenFees} fees={allFees} />
        }
        <Typography variant={smallScreen ?'h6' :'h5'} component='h1' color='primary' sx={sx.header}>Pay Miscellaneous Fees - {courtData.court.courtName}</Typography>
        { logEntry &&
            <Typography variant='h6' color='textSecondary'>
                Call from: {`${logEntry.callerFirstName} ${logEntry.callerLastName}`}
            </Typography>
        }
        <Grid2
            container
            spacing={3}
            justifyContent="center"
        >
            <Backdrop open={loading} sx={sx.backdrop}>
                <CircularProgress color='inherit' />
            </Backdrop>
            <Grid2
                sx={sx.gridBottom}
                size={{
                    xs: 12,
                    md: 4
                }}>
                { newCall ?
                    <EnterResult logEntry={logEntry} setLogEntry={setLogEntry} currentUser={user} setNewCall={setNewCall} /> :
                    <>
                        <Paper elevation={3} sx={sx.controlPanel}>
                            <Typography variant='h6' component='h2' color='primary'>Enter Fee Payment Information</Typography>
                            <Grid2 container spacing={1} alignItems='top'>
                                { allFees &&
                                    <Grid2 size={12}>
                                        <FormControl
                                            component="fieldset"
                                            fullWidth
                                            sx={sx.formControl}
                                            {...register('feeSelect')}
                                        >
                                            <FormLabel component="legend" htmlFor='feeSelect'>
                                                <Typography variant="subtitle1" component='label' for='feeSelect' color='primary'>
                                                    Select a Fee
                                                </Typography>
                                            </FormLabel>
                                            <Select
                                                name='feeSelect'
                                                native
                                                variant='outlined'
                                                fullWidth
                                                sx={{height: '2rem'}}
                                                onChange={feeClicked}
                                                inputProps={{id: 'feeSelect'}}
                                            >
                                                <option key='-1' value=''></option>
                                                {allFees && allFees.map((fee) => (
                                                    <option key={fee.name} value={fee.name} style={{fontSize: '.9rem'}}>{fee.name}</option>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid2>
                                }
                                { deliveryOptions &&
                                    <Grid2 size={12}>
                                        <FormControl
                                            component="fieldset"
                                            fullWidth
                                            sx={sx.formControl}
                                            {...register('optionSelect')}
                                        >
                                            <FormLabel component="legend" htmlFor='optionSelect'>
                                                <Typography variant="subtitle1" component='label' for='optionSelect' color='primary'>
                                                    Select a Delivery Option
                                                </Typography>
                                            </FormLabel>
                                            <Select
                                                name='optionSelect'
                                                native
                                                variant='outlined'
                                                fullWidth
                                                sx={{height: '2rem'}}
                                                onChange={optionClicked}
                                                inputProps={{id: 'optionSelect'}}
                                            >
                                                <option key='-1' value=''>None</option>
                                                {deliveryOptions && deliveryOptions.map((option) => (
                                                    <option key={option.name} value={option.name} style={{fontSize: '.9rem'}}>{option.name}</option>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid2>
                                }
                                <ContactInfo ref={contactRef} internalAgency={user.internalAgency} posPayments={user.posPayments} court={courtData.court} />
                            </Grid2>
                        </Paper>
                        <Box component='div' flexWrap='wrap-reverse' sx={{display: 'flex', flexDirection: 'row-reverse', margin: '1rem', marginBottom: '0'}}>
                            { selectedFees && selectedFees.length > 0 && !selectedFee &&
                                <Button
                                    variant='contained'
                                    color='primary'
                                    startIcon={<PaymentsTwoToneIcon/>}
                                    sx={sx.squashedButton}
                                    onClick={payFees}
                                >
                                    Pay Fees
                                </Button>
                            }
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<ReplayIcon/>}
                                sx={sx.squashedButton}
                                onClick={() => navigate(-1)}
                            >
                                Back
                            </Button>
                            <Button
                                variant='contained'
                                color='primary'
                                startIcon={<ListAltTwoToneIcon />}
                                sx={sx.squashedButton}
                                onClick={() => setOpenFees(true)}
                            >
                                List Fees
                            </Button>
                            { user && user.printUserReceipts &&
                                <UserReceipts />
                            }
                            { logEntry &&
                                <Button
                                    variant='contained'
                                    color='primary'
                                    startIcon={<AddIcCallTwoToneIcon />}
                                    sx={sx.squashedButton}
                                    onClick={() => setNewCall(true)}
                                >
                                    Log Call
                                </Button>
                            }
                        </Box>
                    </>
                }
            </Grid2>
            <Grid2
                size={{
                    xs: 12,
                    md: 8
                }}>
                { errorMessage &&
                    <Alert severity='error' sx={{marginTop: '1rem', textAlign: 'left'}}>
                        <AlertTitle>Error!</AlertTitle>
                        {errorMessage}
                    </Alert>
                }
                <SelectedFees fees={selectedFees} selectedOption={selectedOption} removeSelected={removeSelected} feeTotal={feeTotal} processingFee={processingFee} />
                { selectedFee &&
                    <AddFee payableFee={selectedFee} addSelected={addSelected} cancelAdd={cancelAdd} />
                }
            </Grid2>
        </Grid2>
    </>);
}

export default MiscFees;
